import secureApi from '../../utils/secureApi';

// Feature Toggle interfaces
export interface FeatureToggleCondition {
  name: string;
  comparator: string;
  value: any;
}

export interface FeatureToggle {
  _id: string;
  id?: string; // For display
  featureId: string;
  featureName?: string;
  scope: string;
  clientId: string;
  entityType: string;
  entityValue: string;
  status: 'ENABLED' | 'DISABLED' | 'KILLED';
  conditions?: FeatureToggleCondition[];
  createdAt?: string;
  updatedAt?: string;
}

export interface FeatureToggleListResponse {
  paginatedResults: FeatureToggle[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface CreateFeatureTogglePayload {
  featureId: string;
  scope: string;
  clientId: string;
  entityType: string;
  entityValue: string;
  status: 'ENABLED' | 'DISABLED';
  conditions?: FeatureToggleCondition[];
}

/** Payload for create-or-update (featureName, featureScope, clientId required) */
export interface CreateOrUpdateFeatureTogglePayload {
  featureName: string;
  featureScope: string;
  clientId: string;
  entityType: string;
  entityValue: string;
  enabled: boolean;
  conditions?: { name: string; comparator: string; value: any }[];
  setConditions: boolean;
}

/**
 * List feature toggles with filters
 */
export async function listFeatureToggles(params: {
  clientId?: string; // Optional - pass when required
  scope?: string;
  status?: 'ENABLED' | 'DISABLED';
  featureId?: string;
  page?: number;
  pageSize?: number;
}): Promise<FeatureToggleListResponse> {
  const queryParams = new URLSearchParams();
  
  // Pass clientId in params when required
  if (params.clientId) {
    queryParams.append('clientId', params.clientId);
  }
  
  if (params.scope) queryParams.append('scope', params.scope);
  if (params.status) queryParams.append('status', params.status);
  if (params.featureId) queryParams.append('featureId', params.featureId);
  // Note: page and pageSize are optional - if not provided, API returns all feature toggles
  // Only include pagination params if explicitly provided
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.pageSize !== undefined) queryParams.append('pageSize', params.pageSize.toString());

  const response = await secureApi.get<FeatureToggleListResponse>(
    `/api/v2/admin/feature-management/feature-toggle/list?${queryParams.toString()}`
  );
  
  return response;
}

/**
 * Get a single feature toggle by ID
 */
export async function getFeatureToggle(id: string): Promise<FeatureToggle> {
  const response = await secureApi.get<FeatureToggle>(
    `/api/v2/admin/feature-management/feature-toggle/get/${id}`
  );
  
  return response;
}

/**
 * Create a new feature toggle
 */
export async function createFeatureToggle(
  data: CreateFeatureTogglePayload
): Promise<FeatureToggle> {
  const response = await secureApi.post<FeatureToggle>(
    '/api/v2/admin/feature-management/feature-toggle/create-or-update',
    data
  );
  
  return response;
}

/**
 * Create or update a feature toggle (full payload; featureName, featureScope, clientId required)
 */
export async function createOrUpdateFeatureToggle(
  data: CreateOrUpdateFeatureTogglePayload
): Promise<FeatureToggle> {
  const response = await secureApi.post<FeatureToggle>(
    '/api/v2/admin/feature-management/feature-toggle/create-or-update',
    data
  );
  
  return response;
}

/**
 * Delete a feature toggle by featureId, entityType, entityValue
 */
export async function deleteFeatureToggle(params: {
  featureId: string;
  entityType: string;
  entityValue: string;
}): Promise<void> {
  const query = new URLSearchParams({
    featureId: params.featureId,
    entityType: params.entityType,
    entityValue: params.entityValue,
  });
  await secureApi.delete(
    `/api/v2/admin/feature-management/feature-toggle/delete?${query.toString()}`
  );
}

